<template>
  <div class="card border-0 shadow-sm mb-4">
    <div class="card-body p-4">
      <h2 class="h4 mb-4">Create Short Link</h2>
      
      <form @submit.prevent="handleSubmit" class="mb-4">
        <!-- Link Type Selection -->
        <div class="mb-4">
          <label class="form-label d-block">Link Type</label>
          <div class="link-type-selector">
            <button
              type="button"
              class="type-option"
              :class="{ 'active': form.type === 'redirect' }"
              @click="selectType('redirect')"
              :disabled="isLoading"
            >
              <i class="bi bi-arrow-right-circle fs-3"></i>
              <span class="type-label">Redirect Link</span>
              <small class="type-description">Redirect to any URL</small>
            </button>

            <button
              type="button"
              class="type-option"
              :class="{ 
                'active': form.type === 'file',
                'disabled': !isAuthenticated 
              }"
              @click="selectType('file')"
              :disabled="isLoading || !isAuthenticated"
            >
              <i class="bi bi-file-earmark fs-3"></i>
              <span class="type-label">
                File Link
                <i v-if="!isAuthenticated" class="bi bi-lock-fill ms-1"></i>
              </span>
              <small class="type-description">
                {{ isAuthenticated ? 'Share your uploaded files' : 'Login required' }}
              </small>
            </button>
          </div>
        </div>

        <!-- Redirect URL (for redirect type) -->
        <div v-if="form.type === 'redirect'" class="mb-3">
          <label for="redirectTo" class="form-label">Destination URL</label>
          <input 
            type="url" 
            class="form-control" 
            id="redirectTo" 
            v-model="form.redirectTo"
            required
            placeholder="https://example.com"
            :disabled="isLoading"
          >
        </div>

        <!-- File Selection (for file type) -->
        <div v-if="form.type === 'file'" class="mb-3">
          <label for="fileId" class="form-label">Select File</label>
          <select 
            class="form-select" 
            id="fileId" 
            v-model="form.fileId"
            required
            :disabled="isLoading"
          >
            <option value="">Choose a file...</option>
            <option v-for="file in files" :key="file.file_id" :value="file.file_id">
              {{ file.name }}
            </option>
          </select>
        </div>
        
        <!-- Custom Slug -->
        <div class="mb-3">
          <label for="customSlug" class="form-label">
            Custom Slug (optional)
            <small class="text-muted">- linker.to/your-custom-slug</small>
          </label>
          <input
            type="text"
            class="form-control"
            id="customSlug"
            v-model="form.slug"
            placeholder="my-custom-link"
            pattern="[a-zA-Z0-9-]+"
            :disabled="isLoading"
          >
          <small class="form-text text-muted">
            Only letters, numbers, and hyphens allowed
          </small>
        </div>

        <button 
          type="submit" 
          class="btn btn-primary w-100"
          :disabled="isLoading"
        >
          <span v-if="isLoading" class="spinner-border spinner-border-sm me-2"></span>
          Create Short Link
        </button>

        <div v-if="error" class="alert alert-danger mt-3">
          {{ error }}
        </div>
      </form>
    </div>
  </div>
</template>

<script>
import { defineComponent } from 'vue';
import { mapGetters, mapActions } from 'vuex';

export default defineComponent({
  name: 'ShortLinkCreateForm',

  data() {
    return {
      form: {
        slug: '',
        type: 'redirect',
        redirectTo: '',
        fileId: ''
      }
    };
  },

  computed: {
    ...mapGetters('shortLinks', ['stateIsLoading', 'stateLinkError']),
    ...mapGetters('files', ['stateFiles']),
    ...mapGetters('auth', ['isAuthenticated']),
    files() {
      return this.stateFiles || [];
    },
    isLoading() {
      return this.stateIsLoading;
    },
    error() {
      return this.stateLinkError;
    },
    showFileOption() {
      return this.isAuthenticated;
    }
  },

  methods: {
    ...mapActions('shortLinks', ['createLink']),
    ...mapActions('files', ['getFiles']),

    async handleSubmit() {
      try {
        await this.createLink(this.form);
        // Reset form after successful creation
        this.form = {
          slug: '',
          type: 'redirect',
          redirectTo: '',
          fileId: ''
        };
      } catch (error) {
        console.error('Error creating short link:', error);
      }
    },

    // Reset file selection if user switches away from file type while not authenticated
    handleTypeChange() {
      if (!this.isAuthenticated && this.form.type === 'file') {
        this.form.type = 'redirect';
      }
    },

    selectType(type) {
      if (type === 'file' && !this.isAuthenticated) {
        return;
      }
      this.form.type = type;
      this.handleTypeChange();
    }
  },

  created() {
    // Only load files if authenticated
    if (this.isAuthenticated) {
      console.log("created link create form");
      this.getFiles();
    }
  },

});
</script> 

<style scoped>
.btn-group {
  display: flex;
  gap: 0.5rem;
}

.btn-group .btn {
  flex: 1;
  white-space: nowrap;
}

.form-control:focus,
.form-select:focus {
  box-shadow: 0 0 0 0.25rem rgba(13, 110, 253, 0.15);
}

@media (max-width: 768px) {
  .card-body {
    padding: 1rem !important;
  }
}

.link-type-selector {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 1rem;
  width: 100%;
}

.type-option {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 1.5rem;
  border: 2px solid #e9ecef;
  border-radius: 8px;
  background: white;
  transition: all 0.2s ease;
  cursor: pointer;
  text-align: center;
  height: 100%;
}

.type-option:hover:not(.disabled) {
  border-color: #0d6efd;
  background: #f8f9fa;
}

.type-option.active {
  border-color: #0d6efd;
  background: #f0f7ff;
}

.type-option.disabled {
  opacity: 0.7;
  cursor: not-allowed;
  background: #f8f9fa;
}

.type-label {
  font-weight: 500;
  margin: 0.5rem 0;
  display: flex;
  align-items: center;
  justify-content: center;
}

.type-description {
  color: #6c757d;
  font-size: 0.875rem;
}

/* Responsive adjustments */
@media (max-width: 576px) {
  .link-type-selector {
    grid-template-columns: 1fr;
  }
  
  .type-option {
    padding: 1rem;
  }
}
</style> 